import { useCallback, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { useLocation, useNavigate, useParams } from "react-router"
import { RoleType, StorageKey } from "../../../App/enums"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import SessionService from "../../../App/SessionService"
import { RootState } from "../../../App/store"
import { HapticFeedback } from "../../../App/Helpers/HapticFeedback"
import {
    EdusiiaSmallIcon,
    EventsTabIcon,
    MediaTabIcon,
    MembersTabIcon,
    PostsCommentsTabIcon,
    PostsTabIcon,
    SmallChatIcon,
    SmallChatIconActive,
    SmallNotificationActiveIcon,
    SmallNotificationIcon,
    SmallRoomsIcon,
} from "../Components/SvgIcons"
import "../../../Styles/main.scss"

function MobileBottomNav(props: { isRoomMenu?: boolean }) {
    const navigate = useNavigate()
    const location = useLocation()
    const { id } = useParams()
    const translation = useSelector(selectTranslations)
    const notifications = useSelector((state: RootState) => state.UserNotificationSlice.notifications)
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const hasUnseenMessages = useSelector((state: RootState) => state.chatSlice.hasUnseenMessages)
    const [hasNewNotifications, setHasNewNotifications] = useState<boolean>()
    const organizationId = SessionService.getItem(StorageKey.organizationId)

    useEffect(() => {
        document.body.classList.add("mobile-bottom-nav-visible")

        if (props.isRoomMenu) {
            document.body.classList.add("mobile-room-theme")
        }

        return () => {
            document.body.classList.remove("mobile-bottom-nav-visible")
            document.body.classList.remove("chat-route")
            document.body.classList.remove("mobile-room-theme")
        }
    }, [props.isRoomMenu])

    useEffect(() => {
        const isChatRoute = location.pathname.startsWith("/chat")
        if (isChatRoute) {
            document.body.classList.add("chat-route")
        } else {
            document.body.classList.remove("chat-route")
        }

        return () => {
            document.body.classList.remove("chat-route")
        }
    }, [location.pathname])

    useEffect(() => {
        let newNotifications = notifications.some(notification => notification.read === false)
        setHasNewNotifications(newNotifications)
    }, [notifications])

    const onNavigate = useCallback((path: string) => {
        HapticFeedback.light()
        navigate(path)
        window.scrollTo(0, 0)
    }, [navigate])

    const onNavigationToNotificationsPage = () => {
        switch (role) {
            case RoleType.USER:
                const userPath = "/notifications"
                navigate(userPath)
                window.scrollTo(0, 0)
                break
            case RoleType.ORGANIZATION:
                const organizationPath = `/notifications/${id}`
                navigate(organizationPath)
                window.scrollTo(0, 0)
                break
            default:
                break
        }
    }

    const onPost = () => {
        if(!props.isRoomMenu) {
            const triggerComposer = () => {
            window.dispatchEvent(new CustomEvent("edusiia:open-compose"))
        }

        HapticFeedback.medium()

        if (role === RoleType.ORGANIZATION) {
            const orgId = id || organizationId
            if (!orgId) return
            const dashboardPath = `/organization-dashboard/${orgId}`

            if (location.pathname === dashboardPath) {
                triggerComposer()
            } else {
                navigate(dashboardPath)
                window.setTimeout(triggerComposer, 220)
            }
            return
        }

        if (location.pathname === "/dashboard") {
            triggerComposer()
        } else {
            navigate("/dashboard")
            window.setTimeout(triggerComposer, 220)
        }
        }else{
            onNavigate("?tab=new-post")
        }        
    }

    const isActive = (key: "rooms" | "chat" | "events" | "dashboard" | "notifications" | "room-posts" | "room-members" | "room-media" | "room-events" | "new-post"
    ) => {
        const path = location.pathname

        if (key === "dashboard") {
            return path.startsWith("/dashboard") || path.startsWith("/organization-dashboard")
        }

        if (key === "notifications") {
            return path.startsWith("/notifications")
        }

        if (key === "rooms") {
            return path.startsWith("/all-rooms") || path.startsWith("/organization-rooms") || path === "/rooms" || path.startsWith("/rooms/")
        }

        if (key === "chat") {
            return path.startsWith("/chat")
        }

        if (key === "events") {
            return path.startsWith("/upcoming-events") || path.startsWith("/all-premium-events")
        }

        if (key === "room-posts") {
            if(!location.search.includes("tab")) return true
            return location.search.includes("tab=posts")
        }

        if (key === "room-members") {
            return location.search.includes("tab=members")
        }

        if (key === "room-media") {
            return location.search.includes("tab=media")
        }

        if (key === "room-events") {
            return location.search.includes("tab=events")
        }

        if(key === "new-post") {
            return location.search.includes("tab=new-post")
        }

        return false
    }

    const items = useMemo(() => {
        const base = [
            {
                key: "dashboard",
                label: translation.start || "Dashboard",
                icon: <EdusiiaSmallIcon transparentBackground={Boolean(props.isRoomMenu)} />,
                onClick: () => onNavigate(role === RoleType.ORGANIZATION ? `/organization-dashboard/${id || organizationId}` : "/dashboard"),
            }
        ]

        if(props.isRoomMenu) {
            base.push({
                key: "room-posts",
                label: "Austausch",
                icon: <PostsTabIcon />,
                onClick: () => onNavigate("?tab=posts"),
            },{
                key: "room-members",
                label: "Mitglieder",
                icon: <MembersTabIcon />,
                onClick: () => onNavigate("?tab=members"),
            },
            {
                key: "room-media",
                label: "Mediathek",
                icon: <MediaTabIcon />,
                onClick: () => onNavigate("?tab=media"),
            },
            {
                key: "room-events",
                label: "Events",
                icon: <EventsTabIcon />,
                onClick: () => onNavigate("?tab=events"),
            }
        )
        }

        if (!props.isRoomMenu) {
            base.push({
                key: "notifications",
                label: translation.new || "Neues",
                icon: hasNewNotifications ? <SmallNotificationActiveIcon /> : <SmallNotificationIcon />,
                onClick: () => onNavigationToNotificationsPage(),
            },
                {
                    key: "rooms",
                    label: translation.rooms || "Räume",
                    icon: <SmallRoomsIcon />,
                    onClick: () => onNavigate(role === RoleType.ORGANIZATION ? `/organization-rooms/${id || organizationId}` : "/all-rooms"),
                })

            if (role === RoleType.USER) {
                base.push(
                    {
                        key: "chat",
                        label: translation.chat || "Chat",
                        icon: hasUnseenMessages ? <SmallChatIconActive /> : <SmallChatIcon />,
                        onClick: () => onNavigate("/chat"),
                    },
                )
            }
            base.push({
                key: "events",
                label: "Events",
                icon: <EventsTabIcon />,
                onClick: () => onNavigate(role === RoleType.ORGANIZATION ? `/upcoming-events/${id || organizationId}` : "/upcoming-events"),
            })
        }



        return base
    }, [translation, role, hasUnseenMessages, hasNewNotifications, id, organizationId, onNavigate])

    return (
        <>
            <div
                className={`mobile-bottom-nav ${props.isRoomMenu ? "mobile-bottom-nav--room" : ""}`}
                role="navigation"
                aria-label="Mobile bottom navigation"
                style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
            >
                {items.map((item) => {
                    const active = isActive(item.key as "rooms" | "chat" | "events" | "dashboard")

                    return (
                        <button
                            key={item.key}
                            type="button"
                            className={`mobile-bottom-nav__item ${active ? "mobile-bottom-nav__item--active" : ""}`}
                            onClick={item.onClick}
                        >
                            <div className="mobile-bottom-nav__icon">{item.icon}</div>
                            <div className="mobile-bottom-nav__label">{item.label}</div>
                        </button>
                    )
                })}
            </div>

            {!props.isRoomMenu && (
                <button type="button" className="mobile-bottom-nav__fab" onClick={onPost} aria-label="Post erstellen">
                    <PostsCommentsTabIcon />
                </button>
            )}
        </>
    )
}

export default MobileBottomNav
